import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "../../../actions/actionCreators";
import withModal from "../withModal";
import MaterialModal from "../MaterialModal";
import h from "../../../helpers";
import { toJson } from "../../../helpers/typedHelpers";
import CircularLoading from "../../loading/CircularLoading";

import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { IClientVariable } from "../../../types/stores/vars";
import { IAppState } from "../../../types/stores";

interface IVariableConfirmDeleteModal {
    isOpen: boolean;
    modalProps: {
        entityName: string;
        variable: IClientVariable;
        handleDelete: (id: number) => void;
        scope: string;
    };
}

const VariableConfirmDeleteModal: React.FC<IVariableConfirmDeleteModal> = ({
    modalProps,
    isOpen,
}: IVariableConfirmDeleteModal) => {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [variableIsUsedInLayouts, setVariableIsUsedInLayouts] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [hasErrors, setHasErrors] = useState(false);

    //Props
    const roles = useSelector((state: IAppState) => state.session.roles || []);
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures);

    //Roles
    const hasSuperAdmin = roles.includes("Super Admin");
    const hasExperianAdmin = roles.includes("Experian Admin");

    //Features
    const hasClientVariablesManagement = enabledFeatures.includes("client-variables-management");

    const hide = () => dispatch(hideModal("VARIABLE_ADMIN_CONFIRM_DELETE"));

    const onAfterOpen = () => requestVariableHasObjectReferences();

    const requestVariableHasObjectReferences = () => {
        setLoading(true);
        const getVariableIsUsedInLayouts =
            "/ClientVariable/GetVariableIsUsedInLayouts?" + h.serialize({ variableId: modalProps.variable.Id });
        fetch(getVariableIsUsedInLayouts, { credentials: "same-origin", method: "POST" })
            .then(h.checkStatus)
            .then(toJson)
            .then(data => {
                const response = data as unknown as { variableIsUsedInLayouts: boolean };
                setLoading(false);
                setVariableIsUsedInLayouts(response.variableIsUsedInLayouts);
                setConfirmed(response.variableIsUsedInLayouts == false);
            })
            .catch(error => {
                setLoading(false);
                setHasErrors(true);
                h.error("Error validating " + modalProps.entityName + " references.", error);
            });
    };

    const handleConfirm = () => {
        hide();
        modalProps && modalProps.handleDelete(modalProps.variable.Id);
    };

    const getContent = () => {
        if (!modalProps || !modalProps.entityName || !modalProps.variable) {
            return null;
        }
        let content;

        if (loading) {
            content = (
                <div className="row">
                    <div className="col-sm-12" style={{ display: "flex" }}>
                        {<CircularLoading size="small" />}
                        &nbsp; Validating {modalProps.entityName} references...
                    </div>
                </div>
            );
        } else if (hasErrors) {
            content = (
                <div className="row">
                    <div className="col-sm-12" style={{ display: "flex" }}>
                        {
                            <i
                                className="material-icons"
                                style={{ marginTop: "2px", fontSize: "20px", color: "#bb0048" }}
                            >
                                error
                            </i>
                        }
                        &nbsp;There was an error validating {modalProps.entityName} references, please try again later.
                    </div>
                </div>
            );
        } else if (variableIsUsedInLayouts) {
            content = (
                <div className="row">
                    <div className="col-sm-12">
                        <strong>This {modalProps.entityName} is currently used in layouts.</strong>
                        <br />
                        <br />
                        If deleted, any layouts using it will need to be updated.
                        <br />
                        Also, any history associated with using this {modalProps.entityName} in previous campaigns will
                        be lost.
                        <br />
                        <br />
                        Are you sure you want to delete <strong>{modalProps.variable.VariableName}</strong>?
                        {
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={confirmed}
                                            value={modalProps.variable.VariableName}
                                            style={{ color: "#406eb3", fontSize: "2rem !important" }}
                                            onChange={e => setConfirmed(e.target.checked)}
                                        />
                                    }
                                    label={<span style={{ fontSize: "1em" }}>{"Please check to confirm."}</span>}
                                />
                            </div>
                        }
                    </div>
                </div>
            );
        } else {
            content = (
                <div className="row">
                    <div className="col-sm-12">
                        Are you sure you want to delete {modalProps.entityName}{" "}
                        <strong>{modalProps.variable.VariableName}</strong>?
                    </div>
                </div>
            );
        }
        return content;
    };

    return modalProps.scope != "CampaignFlow" || hasSuperAdmin || hasExperianAdmin || hasClientVariablesManagement ? (
        <MaterialModal
            id="variable-confirm-delete-modal"
            isOpen={isOpen}
            title={"Confirm Delete"}
            onClose={hide}
            onPrimaryButtonRequest={handleConfirm}
            afterOpen={onAfterOpen}
            showPrimaryButton={true}
            isPrimaryButtonDisabled={loading || hasErrors || (variableIsUsedInLayouts && !confirmed)}
            primaryButtonText={"Ok"}
        >
            {getContent()}
        </MaterialModal>
    ) : null;
};

export default withModal(VariableConfirmDeleteModal, "VARIABLE_ADMIN_CONFIRM_DELETE");
